/**
 * OncoAgentInbox — receiving queue + delay-reduction actions (clinic-auth required)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { OaWorkspaceShell } from '../components/OaWorkspaceShell';
import {
  fetchReferralInbox,
  updateReferralInboxStatus,
  postReferralTransition,
  scheduleReferralAppointment,
  logReferralPatientContact,
  fetchTrackingAnalytics,
  fetchTrackingBreaches,
  getClinicSession,
  openReferralFromInbox,
} from '../api/client';
import type { ReferralDoc } from '../api/types';
import { trackOncoAgentPageView } from '../utils/analytics';

import { VERTICALS } from '../verticals/catalog';
import { 
  Clock, 
  Activity, 
  AlertTriangle, 
  ArrowRight, 
  RefreshCw, 
  Search, 
  User, 
  Calendar, 
  FileText, 
  ExternalLink 
} from 'lucide-react';

export function OncoAgentInbox(): React.ReactElement {
  const navigate = useNavigate();
  const [items, setItems] = useState<ReferralDoc[]>([]);
  const [vertical, setVertical] = useState('');
  const [scope, setScope] = useState<'inbox' | 'sent' | 'all'>('inbox');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [schedFor, setSchedFor] = useState<Record<string, string>>({});
  const [schedLoc, setSchedLoc] = useState<Record<string, string>>({});
  const [breaches, setBreaches] = useState<any[]>([]);
  const [showBreachesOnly, setShowBreachesOnly] = useState(false);
  const authed = Boolean(getClinicSession());

  useEffect(() => {
    trackOncoAgentPageView('/oncoagent/inbox', 'OncoAgent Clinic Inbox');
    document.title = 'Clinic inbox · OncoAgent';
  }, []);

  const load = useCallback(async () => {
    if (!getClinicSession()) {
      setLoading(false);
      setItems([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetchReferralInbox({
        vertical: vertical || undefined,
        scope,
      });
      const merged = response.items || [];
      const seen = new Set<string>();
      setItems(
        merged.filter((i) => {
          if (seen.has(i.referral_id)) return false;
          seen.add(i.referral_id);
          return true;
        })
      );
      try {
        setAnalytics(await fetchTrackingAnalytics());
      } catch {
        setAnalytics(null);
      }
      try {
        const b = await fetchTrackingBreaches();
        setBreaches(b.items || []);
      } catch {
        setBreaches([]);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail?.message ||
        (err instanceof Error ? err.message : 'Failed to load inbox');
      setError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [vertical, scope]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id: string, status: string) => {
    setBusyId(id);
    try {
      await updateReferralInboxStatus(id, status);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail?.message || 'Status update failed');
    } finally {
      setBusyId(null);
    }
  };

  const triggerTransition = async (referralId: string, transition: string, payload?: any) => {
    setBusyId(referralId);
    setError(null);
    try {
      await postReferralTransition(referralId, transition, payload);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail?.message || `Transition '${transition}' failed`);
    } finally {
      setBusyId(null);
    }
  };

  const onSchedule = async (referralId: string) => {
    const when = schedFor[referralId];
    if (!when) return;
    setBusyId(referralId);
    try {
      await scheduleReferralAppointment(referralId, {
        scheduled_for: new Date(when).toISOString(),
        location: schedLoc[referralId] || '',
      });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail?.message || 'Schedule failed');
    } finally {
      setBusyId(null);
    }
  };

  const onContact = async (referralId: string) => {
    setBusyId(referralId);
    try {
      await logReferralPatientContact(referralId, {
        channel: 'phone',
        outcome: 'reached',
        notes: 'Logged from clinic inbox',
      });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail?.message || 'Contact log failed');
    } finally {
      setBusyId(null);
    }
  };

  const onOpenReferral = async (referralId: string) => {
    setBusyId(referralId);
    setError(null);
    try {
      const opened = await openReferralFromInbox(referralId);
      navigate(`/oncoagent/case/${opened.case_id}?clinic=1&from=inbox`);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail?.message ||
          err?.message ||
          'Could not open referral workspace'
      );
    } finally {
      setBusyId(null);
    }
  };

  const matchesStatusFilter = (item: ReferralDoc, filter: string): boolean => {
    if (filter === 'ALL') return true;
    const status = item.status || '';
    if (filter === 'NEW') return status === 'SUBMITTED' || status === 'RECEIVED' || status === 'ACKNOWLEDGED';
    if (filter === 'UNDER_REVIEW') return status === 'UNDER_REVIEW';
    if (filter === 'INFORMATION_REQUESTED') return status === 'INFORMATION_REQUESTED';
    if (filter === 'ACCEPTED') return status === 'ACCEPTED';
    if (filter === 'SCHEDULED') return status === 'SCHEDULED';
    if (filter === 'COMPLETED') return status === 'PATIENT_SEEN' || status === 'CLOSED';
    if (filter === 'REDIRECTED_DECLINED') return status === 'REDIRECTED' || status === 'DECLINED' || status === 'WITHDRAWN';
    return true;
  };

  const filteredItems = items.filter((item) => matchesStatusFilter(item, statusFilter));

  if (!authed) {
    return (
      <OaWorkspaceShell
        kicker="Receiving queue"
        title="Clinic referral inbox"
        subtitle="Sign in to your clinic workspace to view tenant-scoped referrals."
      >
        <div className="oa-ws-panel">
          <p className="oa-ws-muted">
            Inbox access requires a clinic session. Cross-clinic PHI is never shown without
            authentication.
          </p>
          <div className="oa-ws-actions">
            <Link className="oa-ws-btn oa-ws-btn-primary" to="/oncoagent/app/clinic">
              Open clinic workspace
            </Link>
          </div>
        </div>
      </OaWorkspaceShell>
    );
  }

  return (
    <OaWorkspaceShell
      kicker="Receiving queue · delay reduction"
      title="Clinic referral inbox"
      subtitle="Open the full referral workspace, acknowledge within SLA, contact patients, schedule consultations, and track real-time clinical milestones."
    >
      {/* Metrics & Analytics Dashboard */}
      {analytics ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
          <div className="bg-white border border-[#e6e2d8] rounded-2xl p-5 shadow-sm relative overflow-hidden flex items-start gap-4">
            <div className="bg-emerald-50 text-[#0f3e17] p-3 rounded-xl">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-semibold text-[#6d6a63] uppercase tracking-wider">Avg days to schedule</p>
              <h3 className="text-3xl font-bold text-[#161616] mt-1 tracking-tight">
                {analytics.avg_days_to_schedule != null ? `${analytics.avg_days_to_schedule}d` : '—'}
              </h3>
              <p className="text-xs text-[#2f6f3e] font-medium mt-1">
                Target: &le; {analytics.target_days_to_consult}d to consult
              </p>
            </div>
          </div>

          <div className="bg-white border border-[#e6e2d8] rounded-2xl p-5 shadow-sm relative overflow-hidden flex items-start gap-4">
            <div className="bg-emerald-50 text-[#0f3e17] p-3 rounded-xl">
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-semibold text-[#6d6a63] uppercase tracking-wider">On track rate</p>
              <h3 className="text-3xl font-bold text-[#161616] mt-1 tracking-tight">
                {analytics.pct_on_track != null ? `${analytics.pct_on_track}%` : '—'}
              </h3>
              <p className="text-xs text-[#6d6a63] mt-1">
                Across <strong className="text-[#161616]">{analytics.count || 0}</strong> tracked referrals
              </p>
            </div>
          </div>

          <div className={`border rounded-2xl p-5 shadow-sm relative overflow-hidden flex items-start gap-4 transition-all duration-300 ${
            breaches.length > 0 
              ? 'bg-rose-50/50 border-rose-200' 
              : 'bg-white border-[#e6e2d8]'
          }`}>
            <div className={`p-3 rounded-xl ${
              breaches.length > 0 ? 'bg-rose-100 text-rose-700' : 'bg-[#f6f4ef] text-[#6d6a63]'
            }`}>
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold text-[#6d6a63] uppercase tracking-wider">SLA breached</p>
              <h3 className={`text-3xl font-bold mt-1 tracking-tight ${
                breaches.length > 0 ? 'text-rose-700' : 'text-[#161616]'
              }`}>
                {breaches.length || (analytics.pct_sla_breached != null ? `${analytics.pct_sla_breached}%` : '—')}
              </h3>
              <button
                type="button"
                className={`text-xs font-semibold mt-1.5 underline underline-offset-2 flex items-center gap-1 transition-colors ${
                  breaches.length > 0 ? 'text-rose-700 hover:text-rose-800' : 'text-[#0f3e17] hover:text-[#0c3312]'
                }`}
                onClick={() => setShowBreachesOnly((v) => !v)}
              >
                <span>{showBreachesOnly ? 'Show full queue' : 'Filter at-risk cases'}</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* At-Risk Queue Banner */}
      {showBreachesOnly && breaches.length > 0 ? (
        <div className="bg-rose-50/80 border border-rose-200 rounded-2xl p-5 mb-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3 text-rose-800">
            <AlertTriangle className="w-5 h-5 text-rose-600 animate-pulse" />
            <h3 className="font-semibold text-base m-0">Critical: SLA-Breached Referrals</h3>
          </div>
          <p className="text-sm text-rose-700 leading-relaxed mb-4">
            The following cases have exceeded active milestone timelines. Please acknowledge receipt or finalize scheduling immediately.
          </p>
          <ul className="space-y-3">
            {breaches.map((b) => (
              <li 
                key={b.tracking_id || b.referral_id || b.case_id} 
                className="bg-white border border-rose-100 rounded-xl p-4 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div>
                  <strong className="text-sm text-[#161616] font-semibold">{b.patient_name || 'Patient'}</strong>
                  <span className="text-xs text-[#6d6a63]"> &middot; Suspected: {b.suspected_cancer_type || b.specialty || 'Oncology'}</span>
                  <span className="bg-rose-100 text-rose-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ml-2">
                    {b.current_label || b.current_milestone || 'At Risk'}
                  </span>
                </div>
                {b.referral_id ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="bg-[#0f3e17] hover:bg-[#0c3312] text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                      disabled={busyId === b.referral_id}
                      onClick={() => onOpenReferral(b.referral_id)}
                    >
                      Open Case
                    </button>
                    <button
                      type="button"
                      className="bg-rose-100 hover:bg-rose-200 text-rose-800 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors border border-rose-200"
                      disabled={busyId === b.referral_id}
                      onClick={() => setStatus(b.referral_id, 'ACKNOWLEDGED')}
                    >
                      Acknowledge
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Structured Filter Workspace Cockpit */}
      <div className="bg-white border border-[#e6e2d8] rounded-2xl p-5 mb-6 shadow-sm space-y-4">
        {/* Tier 1: Triage Scope */}
        <div>
          <span className="text-[10px] font-bold text-[#6d6a63] uppercase tracking-wider block mb-2">Triage queue scope</span>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ['inbox', 'Incoming referrals'],
                ['sent', 'Outgoing referrals'],
                ['all', 'All cases mapped to me'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`text-xs font-medium px-3.5 py-1.5 rounded-full transition-all border ${
                  scope === id 
                    ? 'bg-[#0f3e17] text-white border-[#0f3e17] font-semibold' 
                    : 'bg-[#f6f4ef] text-[#6d6a63] border-[#e6e2d8] hover:text-[#161616]'
                }`}
                onClick={() => setScope(id)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tier 2: Triage Milestones */}
        <div className="border-t border-[#f6f4ef] pt-3">
          <span className="text-[10px] font-bold text-[#6d6a63] uppercase tracking-wider block mb-2">Status / milestone filter</span>
          <div className="flex flex-wrap gap-1.5">
            {[
              { id: 'ALL', name: 'All Active' },
              { id: 'NEW', name: 'New Referrals' },
              { id: 'UNDER_REVIEW', name: 'Under Triage' },
              { id: 'INFORMATION_REQUESTED', name: 'Clarifications Requested' },
              { id: 'ACCEPTED', name: 'Accepted for Consult' },
              { id: 'SCHEDULED', name: 'Scheduled' },
              { id: 'COMPLETED', name: 'Completed Cases' },
              { id: 'REDIRECTED_DECLINED', name: 'Redirected / Declined' },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                className={`text-xs px-3 py-1 rounded-lg transition-colors border ${
                  statusFilter === f.id 
                    ? 'bg-[#0f3e17]/10 text-[#0f3e17] border-[#0f3e17]/30 font-semibold' 
                    : 'bg-white text-[#6d6a63] border-[#e6e2d8] hover:text-[#161616]'
                }`}
                onClick={() => setStatusFilter(f.id)}
              >
                {f.name}
              </button>
            ))}
          </div>
        </div>

        {/* Tier 3: Specialty Verticals */}
        <div className="border-t border-[#f6f4ef] pt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex-1">
            <span className="text-[10px] font-bold text-[#6d6a63] uppercase tracking-wider block mb-2">Clinical Pathway vertical</span>
            <div className="flex flex-wrap gap-1.5">
              {[{ id: '', name: 'All pathways' }, ...VERTICALS].map((v) => (
                <button
                  key={v.id || 'all'}
                  type="button"
                  className={`text-[11px] px-2.5 py-1 rounded-md transition-colors ${
                    vertical === v.id 
                      ? 'bg-[#0f3e17] text-white font-semibold' 
                      : 'bg-white text-[#6d6a63] border border-[#e6e2d8] hover:text-[#161616]'
                  }`}
                  onClick={() => setVertical(v.id)}
                >
                  {v.name}
                </button>
              ))}
            </div>
          </div>
          <button 
            type="button" 
            className="inline-flex items-center gap-1.5 bg-[#f6f4ef] hover:bg-[#ece8df] text-[#161616] text-xs font-semibold px-4 py-2 rounded-xl transition-colors border border-[#e6e2d8] self-end sm:self-auto" 
            onClick={load}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Sync Queue</span>
          </button>
        </div>
      </div>

      {error ? <div className="oa-ws-error">{error}</div> : null}

      {/* Main Referral List Triage Queue */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white border border-[#e6e2d8] rounded-2xl shadow-sm">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f3e17]" />
          <p className="text-sm text-[#6d6a63] mt-3">Fetching clinic registry & secure telemetry...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#e6e2d8] rounded-2xl shadow-sm">
          <Search className="w-10 h-10 text-[#6d6a63]/40 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#161616]">No active referrals matched these filters</p>
          <p className="text-xs text-[#6d6a63] mt-1 max-w-sm mx-auto">
            Try resetting your status and vertical selectors or trigger a manual referral for testing.
          </p>
          <Link 
            className="inline-flex items-center gap-1.5 bg-[#0f3e17] hover:bg-[#0c3312] text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors mt-4" 
            style={{ color: '#ffffff' }}
            to="/oncoagent/app/refer"
          >
            <span>Start a referral</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ) : (
        <ul className="space-y-4">
          {filteredItems.map((item) => {
            const tracking = (item as any).tracking;
            const breached = (item as any).sla_breached || tracking?.metrics?.sla_breached;
            const share = tracking?.share_token;
            
            // Nice color assignment helper for status badges
            const getStatusBadge = (status: string) => {
              const base = "text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1 ";
              if (breached) return base + "bg-rose-100 text-rose-800 border border-rose-200";
              switch(status) {
                case 'SUBMITTED': return base + "bg-blue-50 text-blue-800 border border-blue-100";
                case 'RECEIVED': return base + "bg-[#0f3e17]/10 text-[#0f3e17] border border-[#0f3e17]/20";
                case 'UNDER_REVIEW': return base + "bg-[#c9a227]/10 text-[#9a6700] border border-[#c9a227]/20";
                case 'ACCEPTED': return base + "bg-emerald-50 text-emerald-800 border border-emerald-100";
                case 'SCHEDULED': return base + "bg-purple-50 text-purple-800 border border-purple-100";
                case 'PATIENT_SEEN':
                case 'CLOSED': return base + "bg-gray-100 text-gray-700 border border-gray-200";
                default: return base + "bg-slate-50 text-slate-700 border border-slate-200";
              }
            };

            return (
              <li 
                key={item.referral_id}
                className={`bg-white border rounded-2xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md flex flex-col ${
                  breached ? 'border-rose-200 ring-2 ring-rose-50' : 'border-[#e6e2d8]'
                }`}
              >
                {/* Card Top Block */}
                <div className="p-5 flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#f6f4ef] pb-3 mb-3.5">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-lg text-[#161616] tracking-tight m-0">
                          {item.route?.specialty || 'Oncology Referral'}
                        </h4>
                        <span className="bg-[#f6f4ef] text-[#161616] text-[10px] font-semibold px-2 py-0.5 rounded-md border border-[#e6e2d8]">
                          {item.suspected_cancer_type || 'General suspected'}
                        </span>
                      </div>
                      <p className="text-xs text-[#6d6a63] mt-0.5 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span>GP Ref: <strong className="text-[#161616] font-medium">{item.referring_clinician?.name || 'Dr. Jane Smith'}</strong></span>
                        {item.referring_clinician?.clinic && <span> &middot; {item.referring_clinician.clinic}</span>}
                      </p>
                    </div>

                    <div className="flex flex-col items-end gap-1.5 self-start sm:self-center">
                      <span className={getStatusBadge(item.status || '')}>
                        {item.status}
                        {tracking?.current_label ? ` · ${tracking.current_label}` : ''}
                        {breached && ' · BREACHED'}
                      </span>
                      {typeof item.sla_remaining_hours === 'number' && (
                        <span className="text-[10px] font-semibold text-[#6d6a63] flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          SLA remaining: <strong className={item.sla_remaining_hours <= 24 ? 'text-rose-600' : 'text-[#161616]'}>{item.sla_remaining_hours}h</strong>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Patient Clinical Info Block */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                      <div className="flex items-center justify-between border-b border-dashed border-[#f6f4ef] pb-1.5">
                        <span className="text-[#6d6a63]">Pathway Urgency</span>
                        <strong className={`font-semibold capitalize ${
                          item.route?.urgency === 'urgent' ? 'text-rose-700' : 'text-[#161616]'
                        }`}>
                          {item.route?.urgency || '—'}
                        </strong>
                      </div>
                      <div className="flex items-center justify-between border-b border-dashed border-[#f6f4ef] pb-1.5">
                        <span className="text-[#6d6a63]">Destination Clinic</span>
                        <strong className="text-[#161616] font-semibold">
                          {item.destination_clinic_name || 'Same Clinic / Unassigned Queue'}
                        </strong>
                      </div>
                      {tracking?.metrics?.elapsed_days_since_submit != null && (
                        <div className="flex items-center justify-between border-b border-dashed border-[#f6f4ef] pb-1.5">
                          <span className="text-[#6d6a63]">Elapsed Time</span>
                          <strong className="text-[#161616] font-semibold">
                            {tracking.metrics.elapsed_days_since_submit}d since submit
                          </strong>
                        </div>
                      )}
                    </div>

                    {item.timeline && (
                      <div className="bg-[#f6f4ef]/50 border border-[#e6e2d8] rounded-xl p-3">
                        <span className="text-[9px] font-bold text-[#6d6a63] uppercase tracking-wider block mb-1">GP Triage Timeline</span>
                        <p className="text-xs text-[#161616] leading-relaxed italic">
                          &ldquo;{item.timeline}&rdquo;
                        </p>
                      </div>
                    )}

                    {/* Red Flags Alert Block */}
                    {item.red_flags && item.red_flags.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <span className="text-[9px] font-bold text-rose-700 uppercase tracking-wider mr-1 flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" /> Red flags triggered:
                        </span>
                        {item.red_flags.map((f) => (
                          <span key={f.id || f.flag} className="bg-rose-50 text-rose-700 border border-rose-100 text-[10px] font-semibold px-2 py-0.5 rounded">
                            {f.flag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Scheduling Form Block */}
                  {item.status === 'ACCEPTED' && (
                    <div className="bg-[#0f3e17]/5 border border-[#0f3e17]/10 rounded-xl p-4 mt-4 space-y-3">
                      <div className="flex items-center gap-1.5 text-[#0f3e17]">
                        <Calendar className="w-4 h-4" />
                        <strong className="text-xs font-semibold uppercase tracking-wider">Appointment Triage Desk</strong>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[9px] font-bold text-[#6d6a63] uppercase tracking-wider mb-1">Scheduled for</label>
                          <input
                            className="bg-white border border-[#e6e2d8] rounded-lg px-2.5 py-1.5 text-xs text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50"
                            type="datetime-local"
                            value={schedFor[item.referral_id] || ''}
                            onChange={(e) =>
                              setSchedFor({ ...schedFor, [item.referral_id]: e.target.value })
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold text-[#6d6a63] uppercase tracking-wider mb-1">Consultation Location</label>
                          <input
                            className="bg-white border border-[#e6e2d8] rounded-lg px-2.5 py-1.5 text-xs text-[#161616] w-full focus:outline-none focus:border-[#0f3e17]/50"
                            placeholder="Oncology Wing B, Floor 2"
                            value={schedLoc[item.referral_id] || ''}
                            onChange={(e) =>
                              setSchedLoc({ ...schedLoc, [item.referral_id]: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Card Bottom Actions Panel */}
                <div className="bg-[#f6f4ef]/50 border-t border-[#e6e2d8] px-5 py-3.5 flex flex-wrap gap-2 items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 bg-[#0f3e17] hover:bg-[#0c3312] text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors border border-transparent disabled:opacity-50"
                      disabled={busyId === item.referral_id}
                      onClick={() => onOpenReferral(item.referral_id)}
                    >
                      <FileText className="w-3.5 h-3.5" />
                      <span>{busyId === item.referral_id ? 'Loading...' : 'Open Case'}</span>
                    </button>

                    {item.status === 'SUBMITTED' ? (
                      <>
                        <button
                          type="button"
                          className="bg-white hover:bg-[#ece8df] text-[#161616] text-xs font-semibold px-4 py-2 rounded-lg transition-colors border border-[#e6e2d8]"
                          disabled={busyId === item.referral_id}
                          onClick={() => triggerTransition(item.referral_id, 'receive')}
                        >
                          Acknowledge Receipt
                        </button>
                        <button
                          type="button"
                          className="bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-semibold px-4 py-2 rounded-lg transition-colors border border-rose-200"
                          disabled={busyId === item.referral_id}
                          onClick={() => triggerTransition(item.referral_id, 'decline')}
                        >
                          Decline
                        </button>
                      </>
                    ) : null}

                    {item.status === 'RECEIVED' ? (
                      <button
                        type="button"
                        className="bg-[#0f3e17]/10 hover:bg-[#0f3e17]/20 text-[#0f3e17] text-xs font-semibold px-4 py-2 rounded-lg transition-colors border border-[#0f3e17]/20"
                        disabled={busyId === item.referral_id}
                        onClick={() => triggerTransition(item.referral_id, 'review')}
                      >
                        Start Clinical Review
                      </button>
                    ) : null}

                    {item.status === 'UNDER_REVIEW' ? (
                      <>
                        <button
                          type="button"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
                          disabled={busyId === item.referral_id}
                          onClick={() => triggerTransition(item.referral_id, 'accept')}
                        >
                          Accept Care
                        </button>
                        <button
                          type="button"
                          className="bg-white hover:bg-[#ece8df] text-[#161616] text-xs font-semibold px-4 py-2 rounded-lg transition-colors border border-[#e6e2d8]"
                          disabled={busyId === item.referral_id}
                          onClick={() => {
                            const desc = window.prompt('Describe the clinical information requested from the referring GP:');
                            if (desc?.trim()) {
                              triggerTransition(item.referral_id, 'request_information', {
                                information_request_description: desc.trim(),
                              });
                            }
                          }}
                        >
                          Request Info
                        </button>
                        <button
                          type="button"
                          className="bg-white hover:bg-[#ece8df] text-[#161616] text-xs font-semibold px-4 py-2 rounded-lg transition-colors border border-[#e6e2d8]"
                          disabled={busyId === item.referral_id}
                          onClick={() => {
                            const targetId = window.prompt('Enter target clinic ID to redirect this referral to:');
                            if (targetId?.trim()) {
                              triggerTransition(item.referral_id, 'redirect', {
                                destination_clinic_id: targetId.trim(),
                              });
                            }
                          }}
                        >
                          Redirect
                        </button>
                        <button
                          type="button"
                          className="bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-semibold px-4 py-2 rounded-lg transition-colors border border-rose-200"
                          disabled={busyId === item.referral_id}
                          onClick={() => triggerTransition(item.referral_id, 'decline')}
                        >
                          Decline
                        </button>
                      </>
                    ) : null}

                    {item.status === 'ACCEPTED' ? (
                      <button
                        type="button"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-55 disabled:cursor-not-allowed"
                        disabled={busyId === item.referral_id || !schedFor[item.referral_id]}
                        onClick={() => onSchedule(item.referral_id)}
                      >
                        Schedule Consult Visit
                      </button>
                    ) : null}

                    {item.status === 'SCHEDULED' ? (
                      <button
                        type="button"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
                        disabled={busyId === item.referral_id}
                        onClick={() => triggerTransition(item.referral_id, 'see_patient')}
                      >
                        Complete Consult
                      </button>
                    ) : null}

                    <button
                      type="button"
                      className="bg-white hover:bg-[#ece8df] text-[#6d6a63] hover:text-[#161616] text-xs font-semibold px-4 py-2 rounded-lg transition-colors border border-[#e6e2d8]"
                      disabled={busyId === item.referral_id}
                      onClick={() => onContact(item.referral_id)}
                    >
                      Log Patient Contact
                    </button>

                    {['CLOSED', 'DECLINED', 'REDIRECTED', 'WITHDRAWN'].indexOf(item.status || '') === -1 ? (
                      <button
                        type="button"
                        className="bg-white hover:bg-rose-50 text-rose-700 hover:text-rose-800 text-xs font-semibold px-4 py-2 rounded-lg transition-colors border border-rose-200"
                        disabled={busyId === item.referral_id}
                        onClick={() => triggerTransition(item.referral_id, 'close')}
                      >
                        Close Case
                      </button>
                    ) : null}
                  </div>

                  {/* Portal Integration Links */}
                  <div className="flex items-center gap-2 mt-2 sm:mt-0">
                    {share ? (
                      <Link
                        className="text-xs text-[#0f3e17] hover:underline font-semibold flex items-center gap-1"
                        to={`/oncoagent/track/${share}`}
                        target="_blank"
                      >
                        <span>Patient Portal</span>
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    ) : null}
                    {tracking?.clinic_view_token ? (
                      <Link
                        className="text-xs text-[#0f3e17] hover:underline font-semibold flex items-center gap-1 border-l border-[#e6e2d8] pl-2.5 ml-2.5"
                        to={`/oncoagent/track/${tracking.clinic_view_token}?mode=clinic`}
                        target="_blank"
                      >
                        <span>Quick Track</span>
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </OaWorkspaceShell>
  );
}

export default OncoAgentInbox;
